import React, { useLayoutEffect, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactCrop from 'react-image-crop';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { useSafeTimeout } from '@mm/hooks';
import './CropArea.pcss';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { MODAL_OPEN_BODY_CLASS } from '../../modals/ModalRoot';
import { useKeyDown } from '../../hooks/use-key-down';

const SQUARE_RATIO = 1;
const HORIZONTAL_RATIO = 1.91;

type Props = {
  imgUrl?: string;
  closeCropper: () => any;
  onCropCallback: (blob: Blob) => any;
  fixedRatio?: boolean;
  needsFileModification?: boolean;
};

const getCroppedImg = (
  imgUrl: string,
  image: HTMLImageElement,
  crop: { [key: string]: any },
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');

  return new Promise((resolve, reject) => {
    if (ctx) {
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height,
      );

      const extension = imgUrl.split('.').pop();
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject('toBlob Error');
          }
        },
        `image/${extension}`,
        1,
      );
    } else {
      reject('Canvas not set');
    }
  });
};

const useOverflowBody = () => {
  const { safeTimeout } = useSafeTimeout();
  let originalScrollPosition = 0;
  useLayoutEffect(() => {
    safeTimeout(() => {
      originalScrollPosition = document.documentElement.scrollTop;
      document.body.classList.add(MODAL_OPEN_BODY_CLASS);
    }, 300);

    return () => {
      document.body.classList.remove(MODAL_OPEN_BODY_CLASS);
      document.documentElement.scrollTop = originalScrollPosition;
      originalScrollPosition = 0;
    };
  }, []);
};

export const CropArea = (props: Props) => {
  const {
    imgUrl,
    onCropCallback,
    closeCropper,
    fixedRatio,
    needsFileModification,
  } = props;
  const modifyFile =
    needsFileModification === undefined ? true : needsFileModification;
  const cropRoot = document.getElementById('crop-root');
  const [image, setImage] = useState<HTMLImageElement>();
  const aspect = fixedRatio ? SQUARE_RATIO : HORIZONTAL_RATIO;
  const [crop, setCrop] = useState<ReactCrop.Crop>({
    aspect,
    unit: '%',
    width: 30,
  });
  useOverflowBody();

  const onClickCrop = async () => {
    if (imgUrl && image) {
      const croppedImage = await getCroppedImg(imgUrl, image, crop);

      if (croppedImage) {
        onCropCallback(croppedImage);
        closeCropper();
      }
    }
  };

  const onLoad = useCallback(
    img => {
      setImage(img);
      setCrop({
        unit: '%',
        width: 100,
        aspect,
      });

      return false; // Return false if you set crop state in here.
    },
    [fixedRatio],
  );

  const prepareUrl = useMemo(() => {
    if (!imgUrl) {
      return '';
    }

    const parsedUrl = new URL(imgUrl);

    if (modifyFile) {
      // Workaround CORS problems ( seems cache problems too )
      parsedUrl.searchParams.set('q', '1');
    }
    return parsedUrl.toString();
  }, [imgUrl, modifyFile]);

  useKeyDown((e: KeyboardEvent) => {
    if (e.key === ENTER_KEY_NAME) {
      onClickCrop();
    }
  });

  if (imgUrl && cropRoot) {
    return ReactDOM.createPortal(
      <div className="crop-area">
        <div className="crop-area__overlay" />

        <div className="crop-area__body">
          <div className="crop-area__header-text">Image Crop</div>

          <ReactCrop
            src={prepareUrl}
            crop={crop}
            crossorigin="anonymous"
            onChange={newCrop => setCrop(newCrop)}
            className="crop-area__crop-plugin"
            onImageLoaded={onLoad}
            minWidth={100}
            minHeight={100}
          />

          <div className="crop-area__cropper-controls-container">
            <PrimaryButton
              className="crop-area__button"
              color={ButtonColor.secondaryBlue}
              onClick={closeCropper}
            >
              Cancel
            </PrimaryButton>

            <PrimaryButton className="crop-area__button" onClick={onClickCrop}>
              Crop
            </PrimaryButton>
          </div>
        </div>
      </div>,
      cropRoot,
    );
  }

  return null;
};
